import React, { FC, Fragment } from 'react';
import { Box, Button, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCircle from '@mui/icons-material/AddCircle';

import { SearchSortDirections, SearchSortFields } from 'api';
import { useCheckedState, useGroupSearch } from 'hooks';
import { LoaderComponents } from 'components';
import { GroupCreate } from 'Links';
import { PAGINATION_LIMIT } from 'features/admin/constants';

import { Actions, Table } from '../components/Groups';
import { CheckedMode } from 'types/app';
import theme from 'theme';

const styles = {
  tableHeader: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
} as const;

const Groups: FC = () => {
  const checkedOptions = useCheckedState();
  const { t } = useTranslation();

  const variables = {
    results_from: 0,
    size: PAGINATION_LIMIT,
    auto_exclude: false,
    sort: SearchSortFields.Title,
    sort_direction: SearchSortDirections.Asc,
  };

  const groupSearchQuery = useGroupSearch(variables);

  function renderTotal() {
    if (groupSearchQuery.isInitialLoad) {
      return <LoaderComponents.Block height={24} />;
    }

    return (
      <Typography variant="subtitle2">
        {t('components:xGroup', { count: groupSearchQuery.count })}
      </Typography>
    );
  }

  function getActionVariables() {
    if (checkedOptions.checkedMode === CheckedMode.PAGE) {
      return groupSearchQuery.results.map(result => result.id);
    }

    return checkedOptions.checkedIds;
  }

  return (
    <Fragment>
      <Box paddingBottom={3}>
        <Grid container alignItems="center">
          <Grid xs>
            <Typography variant="h5" color="textSecondary">
              {t('admin:allGroups')}
            </Typography>
          </Grid>
          <Grid>
            <Button
              size="large"
              variant="contained"
              color="secondary"
              component={GroupCreate}
              startIcon={<AddCircle />}
              sx={{ backgroundColor: 'grey.200' }}
            >
              {t('components:newGroup')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Paper>
        <Box padding={2} sx={styles.tableHeader}>
          <Grid container alignItems="center">
            <Grid xs>{renderTotal()}</Grid>
            <Grid>
              <Actions
                resetCheckedState={checkedOptions.resetCheckedState}
                checkedIds={getActionVariables()}
                variables={variables}
              />
            </Grid>
          </Grid>
        </Box>
        <Table variables={variables} {...checkedOptions} {...groupSearchQuery} />
      </Paper>
    </Fragment>
  );
};

export default Groups;
